import type { RefObject, MouseEvent } from "react";
import { memo, useEffect } from "react";
import { ButtonItem } from "./button-item";
import * as Styled from "./style";
import { useRouter } from "next/router";
import { POPULAR_CATEGORIES } from "~/components/blocks/layout/global-navi/const/popular-categories";
import type { SearchHistoryType } from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import type { Hrefs } from "~/const/hrefs";
import { PATHS } from "~/const/paths";

type HistoryListProps = {
  historyListRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLLabelElement>;
  isShowSearchHistory: boolean;
  searchHistories: SearchHistoryType[];
  isViewableUnrestricted: boolean;
  onMouseDownSuggestArea: (e: MouseEvent<HTMLElement>) => void;
  pushToSearch: (arg: SearchHistoryType) => void;
  onClickClearSearchHistory: (index: number) => void;
  onHoldOff: () => void;
};

const _HistoryListItem = (props: HistoryListProps) => {
  const {
    historyListRef,
    inputRef,
    isShowSearchHistory,
    searchHistories,
    isViewableUnrestricted,
    onMouseDownSuggestArea,
    pushToSearch,
    onClickClearSearchHistory,
    onHoldOff,
  } = props;

  const router = useRouter();

  const onNavigate = async (href: Hrefs) => {
    onHoldOff();
    void router.push(href);
  };

  const filteredSearchHistories = isViewableUnrestricted
    ? searchHistories
    : searchHistories.filter((searchHistory) => searchHistory.type !== "gravure");

  /** リスト外をクリックした場合リストを閉じる処理 */
  const outsideListener = () => {
    const body = document.body;

    const formElement = inputRef.current;

    const scope = historyListRef.current;

    if (!scope || !body || !formElement) return;

    const listener = (e: PointerEvent) => {
      setTimeout(() => {
        // 検索フォーム押下時処理処理終了
        if (formElement.contains(e.target as HTMLElement)) return;
        // 履歴リスト外の要素を押下時リストを閉じる
        if (!scope.contains(e.target as HTMLElement)) {
          onHoldOff();
        }
      });
    };

    body.addEventListener("pointerdown", listener, { passive: true });

    return () => {
      body.removeEventListener("pointerdown", listener);
    };
  };

  /** リストTabループ処理(e.preventDefault()実施するのでpassiveは使用しない)*/
  const tabLoop = () => {
    const scope = historyListRef.current;
    if (!scope) return;

    const allButton = scope.querySelectorAll("button");

    const firstButton = allButton[0];

    const lastButton = allButton[allButton.length - 1];

    const loopOnList = (e: KeyboardEvent) => {
      // Escape押下時リスト閉じる
      if (e.key === "Escape") {
        onHoldOff();
      }
      // リスト内最後の要素にfocusがある時最初の要素に戻す
      if (e.key === "Tab") {
        const activeElement = document.activeElement;
        if (activeElement === lastButton) {
          e.preventDefault();
          firstButton.focus();
        }
      }
    };

    scope.addEventListener("keydown", loopOnList);

    return () => {
      scope.removeEventListener("keydown", loopOnList);
    };
  };

  useEffect(outsideListener, []);

  useEffect(tabLoop, []);

  return (
    <Styled.DivSearchHistoryScrollWrapper
      isOpen={isShowSearchHistory}
      ref={historyListRef}
      onMouseDown={onMouseDownSuggestArea}
    >
      <Styled.DivSearchHistoryWrapper>
        {filteredSearchHistories.length !== 0 && (
          <div>
            <Styled.DivHeadingSearchHistory>検索履歴</Styled.DivHeadingSearchHistory>

            <Styled.UlSearchHistoryList>
              {filteredSearchHistories.map((history, index) => (
                <li key={`history-${index}`}>
                  <ButtonItem
                    name={history.keyword}
                    isGravure={history.type === "gravure"}
                    handleClick={() => {
                      pushToSearch({ keyword: history.keyword, type: history.type });
                      onHoldOff();
                    }}
                  />
                  <Styled.ButtonClearSearchHistory
                    iconName="clear"
                    name={"clearSearchHistory"}
                    iconSize="M"
                    iconColor="GRAY900"
                    bgColor="TRANSPARENT"
                    onClick={() => onClickClearSearchHistory(index)}
                  />
                </li>
              ))}
            </Styled.UlSearchHistoryList>
          </div>
        )}

        <div>
          <Styled.DivHeadingSearchHistory>ランキング</Styled.DivHeadingSearchHistory>
          <Styled.UlSearchHistoryList>
            <li>
              <ButtonItem
                name="ビデオ総合ランキング"
                handleClick={() => {
                  onNavigate({
                    pathname: PATHS.VOD_RANKING,
                    query: {
                      category: "",
                    },
                  });
                }}
              />
            </li>
          </Styled.UlSearchHistoryList>
        </div>

        <div>
          <Styled.DivHeadingSearchHistory>ビデオ人気カテゴリ</Styled.DivHeadingSearchHistory>
          <Styled.UlSearchHistoryList>
            {POPULAR_CATEGORIES.map(({ name, queryParam }, index) => (
              <li key={`video-${index}`}>
                <ButtonItem
                  name={name}
                  handleClick={() =>
                    onNavigate({
                      pathname: PATHS.VOD,
                      query: {
                        id: queryParam,
                      },
                    })
                  }
                />
              </li>
            ))}
          </Styled.UlSearchHistoryList>
        </div>
      </Styled.DivSearchHistoryWrapper>
    </Styled.DivSearchHistoryScrollWrapper>
  );
};

export const HistoryListItem = memo(_HistoryListItem);
